import React, { useEffect, useRef, useState } from 'react';
import QuestionsEditor from 'shared/decision/QuestionsEditor';
import { fromApi, toApi } from 'shared/decision/questions';
import { listDecisionIntegrations } from 'shared/decision/decisionApi';
import { listModelCatalog } from 'features/evals3/api/evals3Api';
import VariableEditor, { insertAtCursor } from './VariableEditor';
import SchemaRail from './SchemaRail';
import { useTraceTags, tagGroup, hiddenByTags, withVars, setVars } from './SpanVariables';
import { ProviderDropdown, ModelDropdown } from './modelParts';
import { DEFAULT_DECISION_STATE } from './nodeKinds';
import { stepsGroup } from './graphModel';
import { withQuestions } from './decisionCheck';
import { Section, Field, numInput, codeFrame } from './formParts';

/**
 * A Decision model step: which model (a Jev or Hartley key from Settings → Models),
 * what part of the trace it reads (the state, a template like a judge
 * prompt), and the typed questions it answers. Each question is one output
 * of the step; the routes branch on its outcome.
 */

const DEFAULT_MIN_CONFIDENCE = 0.6;
const newestFirst = (a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || ''));

export default function DecisionEditor({ node, update, schema, theme, readOnly, graph, nodeId, target, teamId, scopes, agentName }) {
  // The team slug is the URL's first segment (/<team>/agent-analytics/evals/...).
  const team = typeof window !== 'undefined' ? window.location.pathname.split('/')[1] || '' : '';
  const cfg = node.config || {};
  const editorRef = useRef(null);
  const before = graph && nodeId ? stepsGroup(graph, nodeId) : [];

  const [integrations, setIntegrations] = useState(null);   // null while loading
  useEffect(() => {
    let off = false;
    Promise.resolve(listDecisionIntegrations(teamId))
      .then((list) => { if (!off) setIntegrations(list || []); })
      .catch(() => { if (!off) setIntegrations([]); });
    return () => { off = true; };
  }, [teamId]);
  const selected = (integrations || []).find((i) => String(i.id) === String(cfg.integration_id ?? ''));

  const [models, setModels] = useState([]);
  const provider = selected?.provider || null;
  useEffect(() => {
    if (!provider) { setModels([]); return undefined; }
    let off = false;
    Promise.resolve(listModelCatalog(teamId, provider))
      .then((list) => { if (!off) setModels((list || []).filter((m) => m.status !== 'coming_soon').map((m) => m.id)); })
      .catch(() => { if (!off) setModels([]); });
    return () => { off = true; };
  }, [teamId, provider]);

  // A check with no model yet takes the team's most recent decision model: one less thing to pick.
  useEffect(() => {
    if (readOnly || cfg.integration_id != null || !integrations || !integrations.length) return;
    const first = [...integrations].sort(newestFirst)[0];
    update({ config: { ...cfg, integration_id: first.id, ...(first.default_model ? { model: first.default_model } : {}) } });
  }, [integrations]); // eslint-disable-line react-hooks/exhaustive-deps

  // The questions are edited as rows (stable ids while a name is half typed) and saved as the API map.
  const [rows, setRows] = useState(() => fromApi(cfg.questions));
  const sent = useRef(JSON.stringify(cfg.questions || {}));
  useEffect(() => {
    const now = JSON.stringify(cfg.questions || {});
    if (now !== sent.current) { sent.current = now; setRows(fromApi(cfg.questions)); }
  }, [cfg.questions]);
  const onRows = (next) => {
    if (readOnly) return;
    setRows(next);
    const questions = toApi(next);
    sent.current = JSON.stringify(questions);
    update(withQuestions(node, questions));
  };

  const abstain = cfg.allow_abstain !== false;   // on unless turned off, as in the TypeSafe console
  const state = typeof cfg.state === 'string' ? cfg.state : (DEFAULT_DECISION_STATE[target] || DEFAULT_DECISION_STATE.run);
  const tags = useTraceTags({
    vars: cfg.span_vars, target, mode: 'prompt', scopes, teamId, agentName,
    getTexts: () => [state],
    apply: ([next], vars) => update({ config: setVars({ ...cfg, state: next }, vars) }),
  });
  const settingsHref = `/${encodeURIComponent(team || '')}/settings/models`;

  return (
    <div data-testid="decision-editor">
      <Section title="Model">
        {integrations && integrations.length === 0 ? (
          <div className="text-[12px] text-[color:var(--text-muted)]">
            Add a Jev or Hartley key in <a href={settingsHref} className="font-medium text-[color:var(--text-primary)] underline-offset-2 hover:underline">Settings → Models</a>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Field label="Decision model">
              <ProviderDropdown integrations={integrations || []} value={cfg.integration_id ?? ''} disabled={readOnly}
                onChange={(id) => {
                  const next = (integrations || []).find((i) => String(i.id) === String(id));
                  const keep = next && next.provider === provider && cfg.model;
                  update({ config: { ...cfg, integration_id: Number(id), model: keep ? cfg.model : (next?.default_model || undefined) } });
                }} />
            </Field>
            <Field label="Model">
              <ModelDropdown integration={selected} models={models} value={cfg.model || selected?.default_model || ''} disabled={readOnly}
                onChange={(m) => update({ config: { ...cfg, model: m } })} />
            </Field>
          </div>
        )}
      </Section>

      <Section title="State">
        <div className={codeFrame}>
          <VariableEditor height="140px" language="markdown" mode="prompt" theme={theme} editorRef={editorRef} readOnly={readOnly}
            onEditVar={readOnly ? null : tags.edit} describeVar={tags.describeVar}
            value={state} onChange={(v) => update({ config: withVars({ ...cfg, state: v }, [v], 'prompt') })} />
        </div>
      </Section>

      <Section title={`Questions ${rows.length}`}>
        <QuestionsEditor rows={rows} onChange={onRows} readOnly={readOnly} showProblems />
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] text-[color:var(--text-primary)]">
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={abstain} disabled={readOnly} className="h-3.5 w-3.5 accent-[color:var(--trodo-primary)]"
              onChange={(e) => {
                const next = { ...cfg, allow_abstain: e.target.checked };
                if (!e.target.checked) delete next.min_confidence;
                update({ config: next });
              }} />
            Allow can't tell
          </label>
          {abstain && (
            <label className="flex items-center gap-2 text-[color:var(--text-secondary)]">
              Minimum confidence
              <input type="number" min={0.05} max={0.95} step={0.05} value={cfg.min_confidence ?? DEFAULT_MIN_CONFIDENCE} readOnly={readOnly}
                aria-label="Minimum confidence" className={numInput}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (Number.isFinite(v)) update({ config: { ...cfg, min_confidence: Math.min(0.95, Math.max(0.05, v)) } });
                }} />
            </label>
          )}
        </div>
      </Section>

      <Section title="Fields">
        <SchemaRail schema={schema} extraGroups={[tagGroup(tags.units, tags.tagRef), ...before].filter(Boolean)} hideTypes={['list']} hidePaths={hiddenByTags(target)}
          refOf={(f) => f.prompt} onPick={(f) => insertAtCursor(editorRef.current, f.prompt)} maxHeight={200} />
      </Section>
      {tags.dialog}
    </div>
  );
}
